<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      show-icon
      title="会员在小程序「资源-协会/商会」主页提交入会申请，通过后成为会员单位（申请状态=joined）；驳回需填写原因，会员在协会主页可见。"
    />
    <el-form :inline="true" :model="query" class="-mb-15px">
      <el-form-item label="审核状态">
        <el-select v-model="query.status" clearable class="!w-140px" placeholder="全部">
          <el-option label="待审核" value="pending" />
          <el-option label="已入会" value="joined" />
          <el-option label="已驳回" value="rejected" />
        </el-select>
      </el-form-item>
      <el-form-item label="协会ID">
        <el-input v-model="query.resourceId" clearable class="!w-160px" placeholder="按资源ID筛选" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">搜索</el-button>
        <el-button @click="reset">重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column prop="resourceName" label="申请加入的协会" min-width="200" show-overflow-tooltip>
        <template #default="{ row }">{{ row.resourceName || '资源#' + row.resourceId }}</template>
      </el-table-column>
      <el-table-column label="申请人" min-width="180">
        <template #default="{ row }">
          <div class="flex items-center gap-8px">
            <el-avatar :size="28" :src="row.userAvatar">
              {{ (row.userNickname || '会').slice(0, 1) }}
            </el-avatar>
            <span>{{ row.userNickname || '未命名会员' }}</span>
            <span class="text-12px text-#909399">#{{ row.userId }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="审核状态" width="100">
        <template #default="{ row }">
          <el-tag :type="statusTag(row.status)" size="small">{{ statusText(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="rejectReason" label="驳回原因" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ row.rejectReason || '-' }}</template>
      </el-table-column>
      <el-table-column label="申请时间" width="170">
        <template #default="{ row }">{{ formatTime(row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="审核时间" width="170">
        <template #default="{ row }">{{ formatTime(row.auditTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <template v-if="row.status === 'pending'">
            <el-button
              v-hasPermi="['bosscircle:resource:audit']"
              link
              type="success"
              @click="handleApprove(row)"
            >
              通过
            </el-button>
            <el-button
              v-hasPermi="['bosscircle:resource:audit']"
              link
              type="danger"
              @click="openReject(row)"
            >
              驳回
            </el-button>
          </template>
          <span v-else class="text-12px text-#909399">已处理</span>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>

  <Dialog title="驳回入会申请" v-model="rejectVisible" width="500px">
    <el-form ref="rejectFormRef" :model="rejectForm" :rules="rejectRules" label-width="90px">
      <el-form-item label="协会">{{ current?.resourceName || '资源#' + current?.resourceId }}</el-form-item>
      <el-form-item label="申请人">{{ current?.userNickname || '未命名会员' }}</el-form-item>
      <el-form-item label="驳回原因" prop="rejectReason">
        <el-input
          v-model="rejectForm.rejectReason"
          type="textarea"
          :rows="3"
          placeholder="请填写驳回原因，将展示给申请人"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="rejectVisible = false">取 消</el-button>
      <el-button :loading="rejecting" type="danger" @click="submitReject">确定驳回</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { formatDate } from '@/utils/formatTime'
import * as Api from '@/api/business/resource'
import type { FormInstance, FormRules } from 'element-plus'

defineOptions({ name: 'BossCircleResourceAssocJoin' })

const route = useRoute()
const message = useMessage()

const loading = ref(false)
const list = ref<Api.ResourceAssocJoinVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  status: (route.query.status as Api.ResourceAssocJoinStatus) || undefined,
  resourceId: (route.query.resourceId as string) || ''
})

/** 入会表的状态词是 joined，不是认领/入驻用的 approved */
const statusText = (s?: string) =>
  ({ pending: '待审核', joined: '已入会', rejected: '已驳回' } as Record<string, string>)[s ?? ''] ||
  '-'
const statusTag = (s?: string) => (s === 'joined' ? 'success' : s === 'rejected' ? 'danger' : 'warning')

const formatTime = (value?: string | number) => (value ? formatDate(value) : '-')

const load = async () => {
  loading.value = true
  try {
    const params: any = { pageNo: query.pageNo, pageSize: query.pageSize }
    if (query.status) params.status = query.status
    if (query.resourceId) params.resourceId = query.resourceId
    const data = await Api.getResourceAssocJoinPage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const search = () => {
  query.pageNo = 1
  load()
}

const reset = () => {
  query.status = undefined
  query.resourceId = ''
  search()
}

const handleApprove = async (row: Api.ResourceAssocJoinVO) => {
  await message.confirm(
    `确认通过「${row.userNickname || row.userId}」加入「${row.resourceName || '该协会'}」的申请吗？`
  )
  await Api.auditResourceAssocJoin({ id: row.id!, approved: true })
  message.success('已通过，该会员已入会')
  load()
}

const rejectVisible = ref(false)
const rejecting = ref(false)
const current = ref<Api.ResourceAssocJoinVO>()
const rejectFormRef = ref<FormInstance>()
const rejectForm = reactive<{ rejectReason: string }>({ rejectReason: '' })
const rejectRules = {
  rejectReason: [{ required: true, message: '驳回原因不能为空', trigger: 'blur' }]
} as FormRules

const openReject = (row: Api.ResourceAssocJoinVO) => {
  current.value = row
  rejectForm.rejectReason = ''
  rejectVisible.value = true
  nextTick(() => rejectFormRef.value?.clearValidate())
}

const submitReject = async () => {
  await rejectFormRef.value?.validate()
  rejecting.value = true
  try {
    await Api.auditResourceAssocJoin({
      id: current.value!.id!,
      approved: false,
      rejectReason: rejectForm.rejectReason.trim()
    })
    message.success('已驳回')
    rejectVisible.value = false
    load()
  } finally {
    rejecting.value = false
  }
}

onMounted(load)
</script>
